import copyIcon from '@jetbrains/icons/copy'
import type {IconType} from '@jetbrains/ring-ui-built/components/icon/icon'
import Tooltip from '@jetbrains/ring-ui-built/components/tooltip/tooltip'
import {IconButton, SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import classNames from 'classnames'
import type {MouseEvent} from 'react'
import {useCallback} from 'react'
import * as React from 'react'

import {useBoolean} from '../../../hooks/useSlice'
import {copyClipboard} from '../../../utils/clipboard'
import isVisualTest from '../../../utils/isVisualTest'
import Popup from '../Popup/Popup.lazy'

import styles from './CopyIconButton.module.css'

type Props = {
  iconClassName?: string
  wrapperClassName?: string
  icon?: IconType | string
  text?: string | undefined | null
  title?: string
  onClick?: React.MouseEventHandler
  beforeCopyContent?: React.ReactNode
  afterCopyContent?: React.ReactNode
  withBorder?: boolean
  dataTestId?: string
  Component?: React.ElementType
}

const isTest = process.env.NODE_ENV === 'test' || isVisualTest()

function CopyIconButton({
  onClick,
  iconClassName,
  wrapperClassName,
  icon = copyIcon,
  text = '',
  title = 'Copy to clipboard',
  beforeCopyContent,
  afterCopyContent,
  withBorder,
  dataTestId = 'copy-button',
  Component,
}: Props) {
  const hasContent = Boolean(beforeCopyContent && afterCopyContent)
  const [copiedToClipboard, setCopiedToClipboard, unsetCopiedToClipboard] = useBoolean(false)

  const popupContent = React.useMemo(() => <span>{'Copied!'}</span>, [])

  const onCopyText = useCallback(
    async (e: MouseEvent<any>) => {
      e.preventDefault()
      e.stopPropagation()

      if (text) {
        await copyClipboard(text)
      }
      onClick?.(e)
      setCopiedToClipboard()
    },
    [text, onClick, setCopiedToClipboard],
  )
  return isTest || (navigator.clipboard != null && (text || hasContent)) ? (
    <div className={classNames(wrapperClassName, styles.wrapper)}>
      <Popup className={styles.popup} hidden={hasContent || !copiedToClipboard} trapFocus={false}>
        {popupContent}
      </Popup>
      <Tooltip
        className={Component ? styles.blockTooltip : undefined}
        title={!copiedToClipboard ? title : ''}
      >
        {Component ? (
          <Component
            role="presentation"
            className={classNames(styles.button, iconClassName)}
            onClick={onCopyText}
            onMouseLeave={unsetCopiedToClipboard}
            onBlur={unsetCopiedToClipboard}
            data-test={dataTestId}
          >
            <SvgIcon icon={icon} />
          </Component>
        ) : (
          <IconButton
            data-test={dataTestId}
            className={iconClassName}
            icon={icon}
            title={!copiedToClipboard ? title : ''}
            onClick={onCopyText}
            onMouseLeave={unsetCopiedToClipboard}
            onBlur={unsetCopiedToClipboard}
            withBorder={withBorder}
          >
            {hasContent && (copiedToClipboard ? afterCopyContent : beforeCopyContent)}
          </IconButton>
        )}
      </Tooltip>
    </div>
  ) : null
}

export default React.memo(CopyIconButton)
